import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormField, ProjectData, FormSection } from '../types';
import { FileUpload } from './FileUpload';
import { HelpTooltip } from './HelpTooltip';
import { UrlField } from './UrlField';

interface EnhancedDynamicFormProps {
  fields: FormField[];
  sections?: FormSection[]; // NEW: Section-based organization
  jiraKey: string;
  readonly?: boolean;
  invalidFields?: string[];
}

export function EnhancedDynamicForm({
  fields,
  sections,
  jiraKey,
  readonly = false,
  invalidFields = [],
}: EnhancedDynamicFormProps) {
  // Use the form context from the parent FormWizard instead of creating our own form
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectData['formData']>();

  // No need for debounced form change since we're using the main form context
  // The FormWizard handles all form state management

  const renderField = (field: FormField) => {
    const fieldError = errors[field.name as keyof typeof errors];
    const highlightClass = invalidFields.includes(field.name) ? 'invalid-field-highlight rounded-lg p-1' : '';

    const wrapField = (content: ReactNode) => (
      <div className={highlightClass}>{content}</div>
    );

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
        return wrapField(
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: controllerField }) => (
                <input
                  {...controllerField}
                  type={field.type}
                  id={field.name}
                  placeholder={field.placeholder}
                  disabled={readonly}
                  value={String(controllerField.value || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
              )}
            />
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      case 'url':
        return wrapField(
          <UrlField
            key={field.name}
            field={field}
            readonly={readonly}
          />
        );
        
      case 'textarea':
        return wrapField(
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: controllerField }) => (
                <textarea
                  {...controllerField}
                  id={field.name}
                  rows={4}
                  placeholder={field.placeholder}
                  disabled={readonly}
                  value={String(controllerField.value || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
              )}
            />
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      case 'select':
        return wrapField(
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: controllerField }) => (
                <select
                  {...controllerField}
                  id={field.name}
                  disabled={readonly}
                  value={String(controllerField.value || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select an option</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            />
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      case 'checkbox':
        return wrapField(
          <div key={field.name} className="flex items-start space-x-3">
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: controllerField }) => (
                <input
                  type="checkbox"
                  id={field.name}
                  disabled={readonly}
                  checked={Boolean(controllerField.value)}
                  onChange={controllerField.onChange}
                  onBlur={controllerField.onBlur}
                  name={controllerField.name}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:bg-gray-100"
                />
              )}
            />
            <div className="space-y-1">
              <label htmlFor={field.name} className="text-sm font-medium text-gray-700">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.helperText && (
                <p className="text-sm text-gray-500">{field.helperText}</p>
              )}
              {field.helpText && <HelpTooltip helpText={field.helpText} />}
            </div>
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
          </div>
        );
        
      case 'file':
        return wrapField(
          <div key={field.name} id={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FileUpload
                  jiraKey={jiraKey}
                  fieldName={field.name}
                  acceptedTypes={field.validation?.fileTypes}
                  maxSize={field.validation?.maxFileSize}
                  onUploadComplete={onChange}
                  currentValue={String(value || '')}
                  readonly={readonly}
                />
              )}
            />
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      case 'color':
        return wrapField(
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center space-x-3">
              <Controller
                name={field.name as keyof ProjectData['formData']}
                control={control}
                render={({ field: controllerField }) => (
                  <input
                    {...controllerField}
                    type="color"
                    id={field.name}
                    disabled={readonly}
                    value={String(controllerField.value || '#000000')}
                    className="h-10 w-16 border border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed"
                  />
                )}
              />
              <Controller
                name={field.name as keyof ProjectData['formData']}
                control={control}
                render={({ field: controllerField }) => (
                  <input
                    {...controllerField}
                    type="text"
                    placeholder="#000000"
                    disabled={readonly}
                    value={String(controllerField.value || '')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  />
                )}
              />
            </div>
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      case 'multiemail':
        return wrapField(
          <div key={field.name} className="space-y-2">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <Controller
              name={field.name as keyof ProjectData['formData']}
              control={control}
              render={({ field: controllerField }) => (
                <textarea
                  id={field.name}
                  rows={3}
                  placeholder={field.placeholder || "email1@example.com, email2@example.com"}
                  disabled={readonly}
                  value={Array.isArray(controllerField.value) ? controllerField.value.join(', ') : String(controllerField.value || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  onChange={(e) => {
                    // Allow free typing - don't process immediately
                    controllerField.onChange(e.target.value);
                  }}
                  onBlur={(e) => {
                    // Process emails only on blur
                    const emails = e.target.value
                      .split(',')
                      .map(email => email.trim())
                      .filter(email => email.length > 0);
                    controllerField.onChange(emails);
                    controllerField.onBlur();
                  }}
                  name={controllerField.name}
                />
              )}
            />
            {fieldError && (
              <p className="text-sm text-red-600">{String(fieldError.message || 'Invalid value')}</p>
            )}
            {field.helperText && (
              <p className="text-sm text-gray-500">{field.helperText}</p>
            )}
            {field.helpText && <HelpTooltip helpText={field.helpText} />}
          </div>
        );
        
      default:
        return wrapField(
          <div key={field.name} className="space-y-2">
            <p className="text-sm text-gray-500">Unsupported field type: {field.type}</p>
          </div>
        );
    }
  };

  // If sections are provided, use section-based rendering
  if (sections && sections.length > 0) {
    // Sort sections by order if provided
    const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
    
    return (
      <div className="space-y-8">
        {sortedSections.map((section) => {
          // Get fields for this section
          const sectionFields = fields.filter(field => section.fields.includes(field.name));
          
          return (
            <div key={section.id} className="bg-gray-50 rounded-lg p-6">
              {/* Section Header */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  {section.icon && <span className="text-lg">{section.icon}</span>}
                  <h3 className="text-lg font-medium text-gray-900">{section.title}</h3>
                </div>
                {section.description && (
                  <p className="text-sm text-gray-600">{section.description}</p>
                )}
              </div>
              
              {/* Section Fields */}
              <div className="space-y-6">
                {sectionFields.map((field) => renderField(field))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  
  // Fallback to flat rendering if no sections provided
  return (
    <div className="space-y-6">
      {fields.map((field) => renderField(field))}
    </div>
  );
}
